import React, { useEffect, useRef, useState } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { galleryData } from '../data/mock';

const GallerySection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const openLightbox = (image, index) => {
    setSelectedImage(image);
    setCurrentIndex(index);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setSelectedImage(null);
    document.body.style.overflow = 'auto';
  };

  const nextImage = () => {
    const newIndex = (currentIndex + 1) % galleryData.images.length;
    setCurrentIndex(newIndex);
    setSelectedImage(galleryData.images[newIndex]);
  };

  const prevImage = () => {
    const newIndex = (currentIndex - 1 + galleryData.images.length) % galleryData.images.length;
    setCurrentIndex(newIndex);
    setSelectedImage(galleryData.images[newIndex]);
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!selectedImage) return;
      if (e.key === 'ArrowRight') {
        const newIndex = (currentIndex + 1) % galleryData.images.length;
        setCurrentIndex(newIndex);
        setSelectedImage(galleryData.images[newIndex]);
      }
      if (e.key === 'ArrowLeft') {
        const newIndex = (currentIndex - 1 + galleryData.images.length) % galleryData.images.length;
        setCurrentIndex(newIndex);
        setSelectedImage(galleryData.images[newIndex]);
      }
      if (e.key === 'Escape') {
        setSelectedImage(null);
        document.body.style.overflow = 'auto';
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedImage, currentIndex]);

  return (
    <section
      id="photos"
      ref={sectionRef}
      className="relative min-h-screen bg-black py-24 overflow-hidden"
    >
      {/* Section header */}
      <div className="max-w-7xl mx-auto px-6 mb-12">
        <span className="text-pink-400 text-xs tracking-[0.3em] block mb-4">PORTFOLIO</span>
        <h2 className={`text-white text-3xl md:text-4xl lg:text-5xl font-extralight tracking-wide mb-4 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          {galleryData.title}
        </h2>
        <p className="text-white/50 text-sm tracking-wide">{galleryData.subtitle}</p>
      </div>

      {/* Gallery grid */}
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {galleryData.images.map((image, index) => (
            <div
              key={image.id}
              className={`relative overflow-hidden cursor-pointer group transition-all duration-700 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'
              }`}
              style={{ transitionDelay: `${index * 100}ms` }}
              onClick={() => openLightbox(image, index)}
            >
              <div className="aspect-[3/4] overflow-hidden">
                <img
                  src={image.src}
                  alt={image.category}
                  className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                />
              </div>
              {/* Overlay */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-300 flex items-end justify-start p-4">
                <span className="text-white text-xs tracking-[0.2em] opacity-0 group-hover:opacity-100 transform translate-y-4 group-hover:translate-y-0 transition-all duration-300">
                  {image.category.toUpperCase()}
                </span>
              </div>
              {/* Pink border on hover */}
              <div className="absolute inset-0 border-2 border-pink-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
            </div>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={closeLightbox}
        >
          {/* Close button */}
          <button
            onClick={closeLightbox}
            className="absolute top-6 right-6 text-white/70 hover:text-pink-400 transition-colors duration-300 z-50"
          >
            <X size={32} />
          </button>

          {/* Navigation arrows */}
          <button
            onClick={(e) => { e.stopPropagation(); prevImage(); }}
            className="absolute left-6 top-1/2 -translate-y-1/2 text-white/70 hover:text-pink-400 transition-colors duration-300 z-50"
          >
            <ChevronLeft size={48} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); nextImage(); }}
            className="absolute right-6 top-1/2 -translate-y-1/2 text-white/70 hover:text-pink-400 transition-colors duration-300 z-50"
          >
            <ChevronRight size={48} />
          </button>

          {/* Image */}
          <div
            className="max-w-5xl max-h-[85vh] px-16"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={selectedImage.src}
              alt={selectedImage.category}
              className="max-w-full max-h-[85vh] object-contain"
            />
            <div className="text-center mt-4">
              <span className="text-pink-400 text-sm tracking-[0.2em]">
                {selectedImage.category.toUpperCase()}
              </span>
              <span className="text-white/40 text-sm ml-4">
                {currentIndex + 1} / {galleryData.images.length}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Bottom decorative line */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
    </section>
  );
};

export default GallerySection;
